import { useState, type ComponentProps, type ReactNode } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@tellescope/ui";
import { EventFormDialogContents } from "./EventFormDialogContent/EventFormDialogContents";
import { AvailabilityFormDialogContents } from "./EventFormDialogContent/AvailabilityFormDialogContents";
import type { EventFormInitialValues } from "../../../hooks";

export interface EventCreateDialogProps extends Omit<ComponentProps<typeof Dialog>, "children"> {
  children?: ReactNode;
  timezone?: string;
  onSuccess?: () => void;
  initialFormValues?: EventFormInitialValues;
  showAvailabilityTab?: boolean;
}

export const EventCreateDialog = ({
  children,
  timezone,
  onSuccess,
  initialFormValues,
  showAvailabilityTab,
  ...rest
}: EventCreateDialogProps) => {
  const [type, setType] = useState<"event" | "availability">("event");

  // Never offer the availability tab for a slot that lies entirely in the past — an availability
  // block is only meaningful if it overlaps the future. With no specific start slot (e.g. the
  // header's "create now" entry point) the slot is inherently current, so the tab stays available.
  const availabilityTabAllowed =
    !!showAvailabilityTab &&
    (() => {
      const startDate = initialFormValues?.startDate;
      if (!startDate) return true;
      const endMs = startDate.getTime() + (initialFormValues?.duration ?? 0) * 60_000;
      return endMs > Date.now();
    })();

  return (
    <Dialog
      {...rest}
      onOpenChange={(open) => {
        if (!open) {
          setType("event");
        }
        rest.onOpenChange?.(open);
      }}
    >
      {!!children && <DialogTrigger>{children}</DialogTrigger>}
      <DialogContent
        data-testid="event-create-dialog"
        showCloseButton={false}
        divided
        overlayVariant="light"
        className="font-light transition-all overflow-hidden max-h-[90svh] flex flex-col"
      >
        {type === "event" ? (
          <EventFormDialogContents
            onSuccess={() => {
              onSuccess?.();
            }}
            onCancel={() => rest.onOpenChange?.(false)}
            timezone={timezone}
            initialFormValues={initialFormValues}
            onAvailabilityTabClick={availabilityTabAllowed ? () => setType("availability") : undefined}
          />
        ) : (
          <AvailabilityFormDialogContents
            onSuccess={() => onSuccess?.()}
            onCancel={() => rest.onOpenChange?.(false)}
            timezone={timezone}
            initialFormValues={initialFormValues}
            onEventTabClick={() => setType("event")}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
